'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '@fshr/context/i18n-context';
import { buildReviewSections } from '../build-review-sections';
import { DefaultReviewStep } from '../default-review-step';
import {
	FormStepChangeReason,
	isReviewStep,
	mergeReviewSections,
	resolveStepId,
	toStepHash,
	type FormStep,
	type PendingNavigation,
	type ResolvedFormStep,
	type StepperFormProps,
} from '../types';

type LeaveStepOptions = {
	markCompleted?: boolean;
	persistValues?: boolean;
};

type CompletionIntent =
	| { type: 'next' }
	| { type: 'jump'; toIndex: number; reason: FormStepChangeReason };

function appendReviewStep(
	steps: FormStep[],
	reviewContent: StepperFormProps['reviewContent'],
	reviewNextButtonText: string | undefined
): FormStep[] {
	if (steps.some(step => isReviewStep(step.stepTitle))) {
		return steps;
	}

	return [
		...steps,
		{
			stepTitle: 'Review',
			stepContent: (reviewContent ?? DefaultReviewStep) as FormStep['stepContent'],
			nextButtonText: reviewNextButtonText,
		},
	];
}

export function useStepperForm({
	steps,
	reviewContent,
	reviewNextButtonText,
	extraReviewSections,
	extraReviewSectionsPosition = 'end',
	onSubmit,
}: Pick<
	StepperFormProps,
	| 'steps'
	| 'reviewContent'
	| 'reviewNextButtonText'
	| 'extraReviewSections'
	| 'extraReviewSectionsPosition'
	| 'onSubmit'
>) {
	const { t } = useI18n();
	const [currentStep, setCurrentStep] = useState(0);
	const [stepIsDirty, setStepIsDirty] = useState(false);
	const [completedSteps, setCompletedSteps] = useState<Set<number>>(() => new Set());
	const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);
	const [initialValues, setInitialValues] = useState<Record<string, string>>({});
	const [draftValues, setDraftValues] = useState<Record<string, string>>({});
	const [showSuccess, setShowSuccess] = useState(false);
	const [submitError, setSubmitError] = useState<string | undefined>();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const completionIntentRef = useRef<CompletionIntent>({ type: 'next' });
	const pendingNavigationRef = useRef<PendingNavigation | null>(null);

	const resetCompletionIntent = () => {
		completionIntentRef.current = { type: 'next' };
	};

	const setPendingJumpOnSubmit = (destination: PendingNavigation) => {
		completionIntentRef.current = {
			type: 'jump',
			toIndex: destination.toIndex,
			reason: destination.reason,
		};
	};

	const clearPendingNavigation = () => {
		pendingNavigationRef.current = null;
		setPendingNavigation(null);
		resetCompletionIntent();
	};

	const onValuesChange = useCallback((patch: Record<string, string>) => {
		setDraftValues(prev => ({ ...prev, ...patch }));
		setStepIsDirty(true);
	}, []);

	const clearDraft = () => {
		setDraftValues({});
		setStepIsDirty(false);
	};

	const resolvedReviewNextButtonText = reviewNextButtonText ?? t('Submit');

	const resolvedSteps: ResolvedFormStep[] = useMemo(() => {
		const allSteps = appendReviewStep(steps, reviewContent, resolvedReviewNextButtonText);
		return allSteps.map((step, index) => ({ ...step, id: resolveStepId(step, index) }));
	}, [steps, reviewContent, resolvedReviewNextButtonText]);

	const activeStep = resolvedSteps[currentStep] ?? resolvedSteps[0];
	const isFirstStep = currentStep === 0;
	const isLastStep = currentStep === resolvedSteps.length - 1;

	useEffect(() => {
		if (typeof window === 'undefined') return;

		const step = resolvedSteps[currentStep];
		if (!step) return;

		const nextHash = `#${toStepHash(step.stepTitle)}`;

		if (window.location.hash !== nextHash) {
			window.history.replaceState(
				null,
				'',
				`${window.location.pathname}${window.location.search}${nextHash}`
			);
		}
	}, [currentStep, resolvedSteps]);

	const leaveStep = async (
		toIndex: number,
		values: Record<string, string>,
		reason: FormStepChangeReason,
		{ markCompleted = false, persistValues = false }: LeaveStepOptions = {}
	) => {
		await activeStep?.onStepChange?.(values, {
			fromIndex: currentStep,
			toIndex,
			reason,
		});
		if (persistValues || markCompleted) {
			setInitialValues(prev => ({ ...prev, ...values }));
		}
		if (markCompleted) {
			setCompletedSteps(prev => new Set(prev).add(currentStep));
		}
		setSubmitError(undefined);
		clearDraft();
		setCurrentStep(toIndex);
	};

	const requestStepChange = (toIndex: number, reason: FormStepChangeReason) => {
		if (toIndex === currentStep) return;
		if (reason === 'jump' && !completedSteps.has(toIndex)) return;

		if ((reason === 'jump' || reason === 'back') && stepIsDirty) {
			const pending = { toIndex, reason };
			pendingNavigationRef.current = pending;
			setPendingNavigation(pending);
			return;
		}

		void leaveStep(toIndex, {}, reason, { markCompleted: reason === 'next' });
	};

	const goToStep = (index: number) => {
		requestStepChange(index, 'jump');
	};

	const handleBack = () => {
		if (isFirstStep) return;
		requestStepChange(currentStep - 1, 'back');
	};

	const handleStepComplete = async (values: Record<string, string>) => {
		const intent = completionIntentRef.current;
		resetCompletionIntent();

		if (intent.type === 'jump') {
			void leaveStep(intent.toIndex, values, intent.reason, {
				markCompleted: true,
				persistValues: true,
			});
			return;
		}

		if (isLastStep) {
			const finalValues = { ...initialValues, ...values };
			setSubmitError(undefined);
			setIsSubmitting(true);
			try {
				await activeStep?.onStepChange?.(values, {
					fromIndex: currentStep,
					toIndex: currentStep,
					reason: 'next',
				});
				await onSubmit(finalValues);
				clearDraft();
				setShowSuccess(true);
			} catch (error) {
				const message =
					error instanceof Error ? error.message : t('Something went wrong. Please try again.');
				setSubmitError(message);
			} finally {
				setIsSubmitting(false);
			}
			return;
		}

		void leaveStep(currentStep + 1, values, 'next', {
			markCompleted: true,
			persistValues: true,
		});
	};

	const handleModalCancel = () => {
		clearPendingNavigation();
	};

	const handleModalDiscard = () => {
		const pending = pendingNavigationRef.current;
		if (!pending) return;
		const { toIndex, reason } = pending;
		clearPendingNavigation();
		void leaveStep(toIndex, {}, reason, { persistValues: false });
	};

	const handleModalSave = (getForm: () => HTMLFormElement | null) => {
		const destination = pendingNavigationRef.current;
		if (!destination) return;

		pendingNavigationRef.current = null;
		setPendingNavigation(null);
		setPendingJumpOnSubmit(destination);

		const form = getForm();
		if (!form) {
			resetCompletionIntent();
			void leaveStep(destination.toIndex, draftValues, destination.reason, {
				persistValues: true,
				markCompleted: true,
			});
			return;
		}

		form.requestSubmit();
		queueMicrotask(() => {
			if (completionIntentRef.current.type === 'jump') {
				resetCompletionIntent();
			}
		});
	};

	const reviewSections = mergeReviewSections(
		buildReviewSections(resolvedSteps, initialValues, goToStep),
		extraReviewSections,
		extraReviewSectionsPosition
	);

	return {
		activeStep,
		resolvedSteps,
		currentStep,
		completedSteps,
		isFirstStep,
		isLastStep,
		isDirty: stepIsDirty,
		pendingNavigation,
		initialValues,
		showSuccess,
		submitError,
		isSubmitting,
		reviewSections,
		onValuesChange,
		goToStep,
		handleBack,
		handleStepComplete,
		handleModalCancel,
		handleModalDiscard,
		handleModalSave,
	};
}
